import { useRouter } from '@application/utils';
import type { CountryCode } from '@gql/graphql';
import type { BreederListPageFormValues } from '@presentation/pages';
import { useCallback, useMemo } from 'react';
import { FORM_INITIAL_VALUES } from './constants';

export const useSearch = () => {
  const { infoFromCurrentPath, navigateToRoute } = useRouter();

  const setSearchFromValues = useCallback(
    (values: BreederListPageFormValues) => {
      const shouldSetBreedsAsQueryParam = values.breedNames && values.breedNames.length > 1;

      const breedPathParam = !shouldSetBreedsAsQueryParam ? values.breedNames?.[0] : null;

      const breedQueryParam = shouldSetBreedsAsQueryParam
        ? values.breedNames?.map((x) => encodeURI(x))
        : null;

      const searchObject = {
        page: 1,
        levels: values.levels,
        ...(values.hasLitters != null && { hasLitters: values.hasLitters.toString() }),
        ...(values.spokenLanguages?.length && { spokenLanguages: values.spokenLanguages }),
        pedigree: values.pedigree,
        countries: values.countries,
        regions: values.regions,
        ...(shouldSetBreedsAsQueryParam ? { breeds: breedQueryParam } : {}),
      };

      navigateToRoute('breederList', {
        params: breedPathParam ? [encodeURI(breedPathParam)] : [],
        search: searchObject,
      });
    },
    [navigateToRoute],
  );

  const valuesFromSearch = useMemo((): Partial<BreederListPageFormValues> => {
    const { breeds, hasLitters, spokenLanguages, levels, regions, pedigree, countries } =
      infoFromCurrentPath.search ?? {};

    const breedFromPathParam = infoFromCurrentPath.params?.length
      ? [decodeURI(infoFromCurrentPath.params[0])]
      : null;

    const breedNames =
      breedFromPathParam ?? (breeds as string[]) ?? (FORM_INITIAL_VALUES.breedNames as string[]);

    return {
      ...(hasLitters === 'true' && { hasLitters: true }),
      ...(hasLitters === 'false' && { hasLitters: false }),
      breedNames,
      levels: (levels as string[]) ?? FORM_INITIAL_VALUES.levels,
      spokenLanguages: spokenLanguages as string[] | undefined,
      pedigree: (pedigree as string[]) ?? FORM_INITIAL_VALUES.pedigree,
      countries: (countries as CountryCode[]) ?? FORM_INITIAL_VALUES.countries,
      regions: (regions as string[]) ?? FORM_INITIAL_VALUES.regions,
    };
  }, [infoFromCurrentPath.params, infoFromCurrentPath.search]);

  return { setSearchFromValues, valuesFromSearch };
};
